/**
 * usePrintQueue.js
 *
 * Hook para la cola de impresión en tiempo real.
 * Abre una conexión SSE al backend y cuando llega una nueva venta,
 * la imprime automáticamente usando el navegador de ESTA PC.
 *
 * Solo activo si:
 *   - El usuario ha habilitado "Impresora local" en ajustes
 *   - Hay un token de autenticación disponible
 *
 * Uso:
 *   const { printerOnline, isListening, testPrint } = usePrintQueue({ enabled: true });
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import printerService from '../services/printerService';
import api from '../api/api';

const SSE_URL = `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3002'}/api/print-queue/subscribe`;

const usePrintQueue = ({ enabled = false } = {}) => {
  const [isListening, setIsListening] = useState(false);
  const [printerOnline, setPrinterOnline] = useState(false);
  const [lastPrintedSale, setLastPrintedSale] = useState(null);
  const [printCount, setPrintCount] = useState(0);
  const [error, setError] = useState(null);

  const esRef = useRef(null);
  const retryTimeout = useRef(null);
  const retryCount = useRef(0);
  const mountedRef = useRef(true);
  // Set de IDs de ventas impresas recientemente para evitar doble-impresión
  // cuando la venta local genera evento SSE new_sale en el mismo navegador.
  const recentlyPrinted = useRef(new Set());

  // Obtener token del localStorage — misma clave que usa api.js interceptor
  const getToken = () => {
    try {
      return (
        localStorage.getItem('token') ||
        localStorage.getItem('posent_access_token') ||
        localStorage.getItem('posent_admin_token') ||
        null
      );
    } catch {
      return null;
    }
  };

  /**
   * Imprime una venta usando el mismo servicio que el botón de imprimir manual.
   * Omite la venta si fue impresa en los últimos 10 segundos (deduplicación).
   */
  const printSale = useCallback(async (sale) => {
    try {
      const saleKey = sale?._id || sale?.numero_venta || JSON.stringify(sale?.total);
      if (saleKey && recentlyPrinted.current.has(saleKey)) {
        console.log('[PrintQueue] ⏭️ Venta ya impresa recientemente, omitiendo duplicado:', saleKey);
        return;
      }
      if (saleKey) {
        recentlyPrinted.current.add(saleKey);
        // Limpiar del set luego de 10s para permitir reimpresiones manuales
        setTimeout(() => recentlyPrinted.current.delete(saleKey), 10000);
      }

      console.log('[PrintQueue] 🖨️ Imprimiendo venta automática:', sale?.numero_venta);
      
      // Usar printTicket — el método central de printerService para facturas/tickets
      await printerService.printTicket(sale, {});
      
      setLastPrintedSale(sale);
      setPrintCount(c => c + 1);
      console.log('[PrintQueue] ✅ Venta impresa:', sale?.numero_venta);
    } catch (err) {
      console.error('[PrintQueue] ❌ Error al imprimir venta automática:', err);
    }
  }, []);

  /**
   * Conectar usando EventSource nativo (estándar para Server-Sent Events de larga duración).
   */
  const connectWithEventSource = useCallback((token) => {
    if (!mountedRef.current) return;

    try {
      const apiBase = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3002';
      const sseUrl = `${apiBase}/api/print-queue/subscribe?token=${encodeURIComponent(token)}`;

      const es = new EventSource(sseUrl);
      esRef.current = es;

      es.addEventListener('connected', (e) => {
        if (!mountedRef.current) return;
        setIsListening(true);
        setPrinterOnline(true);
        setError(null);
        retryCount.current = 0;
        console.log('[PrintQueue] 🟢 Conectado al servidor SSE:', e.data);
      });

      es.addEventListener('new_sale', (e) => {
        try {
          const parsed = JSON.parse(e.data);
          if (parsed.sale) {
            console.log('[PrintQueue] 📄 Nueva venta recibida para imprimir:', parsed.sale.numero_venta);
            printSale(parsed.sale);
          }
        } catch (err) {
          console.warn('[PrintQueue] Error procesando evento new_sale:', err);
        }
      });

      es.onerror = () => {
        if (!mountedRef.current) return;
        console.warn('[PrintQueue] ⚠️ Desconectado de SSE, reintentando automáticamente...');
        setIsListening(false);
        setPrinterOnline(false);
        setError('Reconectando...');
      };
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn('[PrintQueue] Error iniciando EventSource:', err.message);
      setIsListening(false);
      setPrinterOnline(false);
      setError(err.message);
    }
  }, [printSale]);

  /**
   * Conectar al SSE del backend.
   */
  const connect = useCallback(() => {
    if (!enabled || !mountedRef.current) return;

    const token = getToken();
    if (!token) {
      console.warn('[PrintQueue] Sin token de autenticación, no se puede conectar');
      setError('Sin sesión activa');
      return;
    }

    // Cerrar conexión anterior
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    connectWithEventSource(token);
  }, [enabled, connectWithEventSource]);

  // Arrancar/parar escucha cuando cambia `enabled`
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      const token = getToken();
      if (token) {
        connectWithEventSource(token);
      } else {
        setError('Sin sesión activa. Iniciá sesión primero.');
      }
    }

    return () => {
      mountedRef.current = false;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      setIsListening(false);
      setPrinterOnline(false);
    };
  }, [enabled]);

  /**
   * Enviar un ticket de prueba para verificar que el sistema funciona.
   */
  const testPrint = useCallback(async () => {
    try {
      const response = await api.post('/print-queue/test');
      return response.data;
    } catch (err) {
      console.error('[PrintQueue] Error enviando prueba:', err);
      throw err;
    }
  }, []);

  /**
   * Verificar cuántos dispositivos están suscritos.
   */
  const checkStatus = useCallback(async () => {
    try {
      const response = await api.get('/print-queue/status');
      return response.data?.data;
    } catch (err) {
      return null;
    }
  }, []);

  return {
    isListening,
    printerOnline,
    lastPrintedSale,
    printCount,
    error,
    testPrint,
    checkStatus
  };
};

export default usePrintQueue;
